import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Search, Printer, Receipt, CreditCard, Banknote, Building, Filter, Download, Mail, Trash2, AlertTriangle, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../api/axios';

const METHOD_ICONS = {
  CB: CreditCard,
  Espece: Banknote,
  Virement: Building,
  Cheque: Receipt,
  'Cheque Restaurant': Receipt,
};

const formatDateGuyane = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { timeZone: 'America/Cayenne' });
};
const formatTimeGuyane = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Cayenne' });
};
const getSessionKey = (p) => {
  const d = new Date(p.created_at);
  const guyane = new Date(d.getTime() - 3 * 60 * 60 * 1000);
  return `${p.table_number}-${guyane.toISOString().slice(0, 10)}`;
};

const PaymentHistory = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [emailModal, setEmailModal] = useState(null); // {type:'session'|'individual', payment?, session?}
  const [emailAddress, setEmailAddress] = useState('');
  const [clientName, setClientName] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expandedSessions, setExpandedSessions] = useState({});
  const isWebmaster = localStorage.getItem('staffRole') === 'webmaster';
  const isAdmin = localStorage.getItem('staffRole') === 'admin';
  const canDelete = isWebmaster || isAdmin;
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [loadingTrash, setLoadingTrash] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [printerConfig, setPrinterConfig] = useState({ font_size: 14, print_logo: true });

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchPayments();
    api.get('/printer/config').then(({ data }) => setPrinterConfig(data)).catch(() => {});
  }, [navigate]);

  const fetchPayments = async (from, to, method) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from || dateFrom) params.append('date_from', from || dateFrom);
      if (to || dateTo) params.append('date_to', to || dateTo);
      if ((method || methodFilter) && (method || methodFilter) !== 'all') params.append('method', method || methodFilter);
      const res = await api.get(`/payments?${params.toString()}`);
      setPayments(res.data);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les paiements', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const applyFilters = () => fetchPayments(dateFrom, dateTo, methodFilter);
  const resetFilters = () => { setDateFrom(''); setDateTo(''); setMethodFilter('all'); setSearch(''); fetchPayments('', '', 'all'); };

  const filteredPayments = search
    ? payments.filter(p =>
        (p.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.receipt_number || '').toLowerCase().includes(search.toLowerCase()) ||
        String(p.table_number).includes(search)
      )
    : payments;

  // Group by session (table + date)
  const sessions = {};
  filteredPayments.forEach(p => {
    const key = getSessionKey(p);
    if (!sessions[key]) sessions[key] = { table_number: p.table_number, date: formatDateGuyane(p.created_at), payments: [], total: 0 };
    sessions[key].payments.push(p);
    sessions[key].total += p.amount || 0;
  });
  const sessionList = Object.entries(sessions).sort((a, b) => {
    const da = a[1].payments[0]?.created_at || '';
    const db2 = b[1].payments[0]?.created_at || '';
    return db2 > da ? 1 : -1;
  });

  const totalFiltered = filteredPayments.reduce((s, p) => s + (p.amount || 0), 0);

  const toggleSession = (key) => setExpandedSessions(prev => ({ ...prev, [key]: !prev[key] }));

  const handlePrintReceipt = (payment) => {
    const w = window.open('', '_blank', 'width=302,height=600');
    const logoUrl = `${window.location.origin}/logo-taverne.png`;
    const legacy = { small: 11, normal: 14, large: 18 };
    const parsedFontSize = Number(printerConfig.font_size);
    const baseSize = Math.min(32, Math.max(11, Number.isFinite(parsedFontSize) ? parsedFontSize : (legacy[printerConfig.font_size] || 14)));
    const logoHtml = printerConfig.print_logo !== false
      ? `<img class="logo" src="${logoUrl}" onerror="this.style.display='none'" />`
      : '';
    const allItems = payment.items || [];
    const plats = allItems.filter(i => i.type !== 'boisson');
    const boissons = allItems.filter(i => i.type === 'boisson');
    const groupByCategory = (items) => {
      const groups = {};
      items.forEach(i => { const cat = i.category_name || 'Autres'; if (!groups[cat]) groups[cat] = []; groups[cat].push(i); });
      return groups;
    };
    const renderGroup = (items) => items.map(i => {
      const sub = (parseFloat(i.price)*i.quantity).toFixed(2);
      let details = '';
      if (i.cuisson) details += `<tr><td></td><td class="detail" colspan="2">Cuisson: ${i.cuisson}</td></tr>`;
      if (i.sauce) details += `<tr><td></td><td class="detail" colspan="2">Sauce: ${i.sauce}</td></tr>`;
      return `<tr class="item-row"><td class="item-qty">${i.quantity}x</td><td class="item-name">${i.name}</td><td class="item-price">${sub}</td></tr>${details}`;
    }).join('');
    const renderSection = (title, items) => {
      if (items.length === 0) return '';
      const cats = groupByCategory(items);
      let html = `<tr><td colspan="3" class="section-title">${title}</td></tr>`;
      Object.entries(cats).forEach(([catName, catItems]) => {
        if (Object.keys(cats).length > 1) html += `<tr><td colspan="3" class="cat-title">${catName}</td></tr>`;
        html += renderGroup(catItems);
      });
      return html;
    };
    const itemsHtml = renderSection('PLATS', plats) + renderSection('BOISSONS', boissons);
    const dateStr = new Date(payment.created_at).toLocaleString('fr-FR', { timeZone: 'America/Cayenne' });
    w.document.write(`<!DOCTYPE html><html><head><style>
      @page{size:70mm auto;margin:0}
      *{margin:0;padding:0;box-sizing:border-box;font-weight:900 !important}
      body{font-family:'Courier New',Courier,monospace;width:66mm;margin:1mm 2mm;font-size:${baseSize}px;color:#000;line-height:1.3;-webkit-print-color-adjust:exact}
      .header{text-align:center;padding-bottom:3px;border-bottom:2px double #000}
      .logo{width:22mm;height:auto;margin:0 auto 2px;display:block}
      .brand{font-size:${baseSize + 5}px;letter-spacing:2px;margin:2px 0 1px}
      .subtitle{font-size:${Math.max(8, baseSize - 2)}px}
      .sep{border:none;border-top:1px dashed #000;margin:3px 0}
      .meta{font-size:${Math.max(8, baseSize - 1)}px;line-height:1.4;padding:2px 0}
      .items-table{width:100%;border-collapse:collapse;margin:3px 0}
      .section-title{font-size:${baseSize + 1}px;text-align:center;padding:4px 0 2px;border-bottom:2px solid #000;letter-spacing:2px}
      .cat-title{font-size:${Math.max(8, baseSize - 1)}px;padding:3px 0 1px;font-style:italic;border-bottom:1px solid #000}
      .item-row td{padding:2px 0;border-bottom:1px dotted #000;vertical-align:top}
      .item-qty{width:18px;font-size:${baseSize}px}
      .item-name{font-size:${baseSize}px;word-break:break-word}
      .item-price{width:42px;font-size:${baseSize}px;text-align:right;white-space:nowrap}
      .detail{font-size:${Math.max(8, baseSize - 2)}px;padding:0 0 2px 0;border:none !important}
      .total-box{text-align:center;border:2px solid #000;padding:4px 2px;margin:4px 0}
      .total-label{font-size:${baseSize}px;letter-spacing:2px}
      .total-amount{font-size:${baseSize + 7}px;letter-spacing:1px}
      .info{text-align:center;font-size:${Math.max(8, baseSize - 1)}px;line-height:1.4;padding:2px 0}
      .footer{text-align:center;margin-top:4px;padding-top:3px;border-top:2px double #000}
      .thanks{font-size:11px}
      .legal{font-size:9px;margin-top:2px}
    </style></head><body>
      <div class="header">
        ${logoHtml}
        <div class="brand">LA TAVERNE</div>
        <div class="subtitle">RESTAURANT & GRILLADES</div>
      </div>
      <hr class="sep">
      <div class="meta">
        <div><b>N:</b> ${payment.receipt_number}</div>
        <div><b>Table:</b> ${payment.table_number} | ${payment.customer_name || '-'}</div>
        <div><b>Date:</b> ${dateStr}</div>
      </div>
      <table class="items-table">${itemsHtml}</table>
      <div class="total-box">
        <div class="total-label">TOTAL</div>
        <div class="total-amount">${payment.amount?.toFixed(2)} EUR</div>
      </div>
      <div class="info">
        <div><b>Mode:</b> ${payment.method}</div>
        <div>Encaisse par: ${payment.created_by_name || payment.created_by || '-'}</div>
        ${payment.amount_given ? `<div>Recu: ${payment.amount_given?.toFixed(2)} | Monnaie: ${(payment.change_due || 0).toFixed(2)}</div>` : ''}
      </div>
      <div class="footer">
        <div class="thanks">Merci de votre visite !</div>
        <div class="legal">Exo. TVA - Art. 294 CGI</div>
      </div>
    </body></html>`);
    w.document.close(); w.print();
  };

  const exportCSV = () => {
    const headers = ['N Recu', 'Date', 'Table', 'Client', 'Montant', 'Mode', 'Caissier'];
    const rows = filteredPayments.map(p => [
      p.receipt_number, new Date(p.created_at).toLocaleString('fr-FR', { timeZone: 'America/Cayenne' }), p.table_number,
      p.customer_name || '-', p.amount?.toFixed(2), p.method, p.created_by_name || p.created_by || '-',
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `paiements_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  const handleDeletePayment = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/payments/${deleteModal.id}`);
      toast({ title: 'Corbeille', description: `Paiement ${deleteModal.receipt_number} deplace dans la corbeille` });
      setPayments(prev => prev.filter(p => p.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de supprimer', variant: 'destructive' });
    } finally { setDeleting(false); }
  };

  const fetchTrash = async () => {
    setLoadingTrash(true);
    try {
      const res = await api.get('/payments/trash');
      setTrashItems(res.data);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger la corbeille', variant: 'destructive' });
    } finally { setLoadingTrash(false); }
  };

  const handleRestore = async (trashId) => {
    setRestoringId(trashId);
    try {
      await api.post(`/payments/trash/${trashId}/restore`);
      toast({ title: 'Restaure', description: 'Paiement restaure avec succes' });
      setTrashItems(prev => prev.filter(t => t.id !== trashId));
      fetchPayments();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de restaurer', variant: 'destructive' });
    } finally { setRestoringId(null); }
  };

  const handleResetAll = async () => {
    setResetting(true);
    try {
      const res = await api.delete('/payments/reset-all');
      toast({ title: 'Reinitialise', description: `${res.data.deleted_count} paiement(s) supprime(s)` });
      setPayments([]); setResetModal(false);
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Impossible de reinitialiser', variant: 'destructive' });
    } finally { setResetting(false); }
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !clientName.trim() || !emailModal) return;
    setSendingEmail(true);
    try {
      if (emailModal.type === 'session') {
        // Send complete session invoice
        await api.post('/payments/send-session-receipt', {
          email: emailAddress,
          client_name: clientName.trim(),
          payment_ids: emailModal.session.payments.map(p => p.id),
        });
        toast({ title: 'Facture complete envoyee', description: `Facture de session Table ${emailModal.session.table_number} envoyee a ${emailAddress}` });
      } else {
        // Send individual invoice
        await api.post(`/payments/${emailModal.payment.id}/send-receipt`, {
          email: emailAddress,
          client_name: clientName.trim(),
        });
        toast({ title: 'Facture individuelle envoyee', description: `Facture ${emailModal.payment.receipt_number} envoyee a ${emailAddress}` });
      }
      setEmailModal(null); setEmailAddress(''); setClientName('');
    } catch {
      toast({ title: 'Erreur', description: "Impossible d'envoyer la facture", variant: 'destructive' });
    } finally { setSendingEmail(false); }
  };

  const openEmailChoice = (session) => {
    if (session.payments.length === 1) {
      // Only one payment, send directly
      setEmailModal({ type: 'individual', payment: session.payments[0] });
    } else {
      // Multiple payments, show choice
      setEmailModal({ type: 'choice', session });
    }
    setEmailAddress(''); setClientName('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" data-testid="payment-history-title">Historique des Paiements</h1>
            <p className="text-xs text-gray-300 mt-0.5">{sessionList.length} session(s) | {filteredPayments.length} paiement(s) | Total: {totalFiltered.toFixed(2)} EUR</p>
          </div>
          <div className="flex gap-2">
            {isWebmaster && (
              <>
                <Button onClick={() => { setTrashOpen(true); fetchTrash(); }} size="sm" className="bg-amber-600 hover:bg-amber-700 text-xs" data-testid="trash-btn">
                  <Trash2 size={14} className="mr-1" /> Corbeille
                </Button>
                <Button onClick={() => setResetModal(true)} size="sm" className="bg-red-900 hover:bg-red-950 text-xs border border-red-700" data-testid="reset-all-btn">
                  <AlertTriangle size={14} className="mr-1" /> RAZ
                </Button>
              </>
            )}
            <Button onClick={exportCSV} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="export-csv-btn">
              <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button onClick={() => navigate('/staff/orders')} size="sm" className="bg-orange-600 hover:bg-orange-700 text-xs">Commandes</Button>
            <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs">
              <ArrowLeft size={14} className="mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Filters */}
        <Card className="mb-4 border border-gray-200" data-testid="filters-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Du</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40 text-sm" data-testid="filter-date-from" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Au</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40 text-sm" data-testid="filter-date-to" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Mode</label>
                <Select value={methodFilter} onValueChange={setMethodFilter}>
                  <SelectTrigger className="h-9 w-44 text-sm" data-testid="filter-method"><SelectValue placeholder="Tous" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="CB">Carte Bancaire</SelectItem>
                    <SelectItem value="Espece">Espece</SelectItem>
                    <SelectItem value="Virement">Virement</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Cheque Restaurant">Cheque Restaurant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={applyFilters} size="sm" className="bg-[#2c1810] hover:bg-[#4a2c1a] h-9" data-testid="apply-filters-btn">
                <Filter size={14} className="mr-1" /> Filtrer
              </Button>
              <Button onClick={resetFilters} variant="outline" size="sm" className="h-9" data-testid="reset-filters-btn">Reinitialiser</Button>
              <div className="ml-auto">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="h-9 pl-8 w-52 text-sm" data-testid="search-input" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sessions List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : sessionList.length === 0 ? (
          <div className="text-center py-12">
            <Receipt size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-lg text-gray-400">Aucun paiement trouve</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessionList.map(([key, session]) => {
              const expanded = expandedSessions[key];
              const methods = [...new Set(session.payments.map(p => p.method))];
              return (
                <div key={key} className="bg-white border border-stone-200 rounded-xl shadow-sm overflow-hidden" data-testid={`session-${key}`}>
                  {/* Session header */}
                  <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-stone-50 transition-colors" onClick={() => toggleSession(key)} data-testid={`session-toggle-${key}`}>
                    <div className="flex items-center justify-center w-12 h-12 bg-[#f5f0ea] rounded-xl text-lg font-bold text-[#2c1810]">
                      T{session.table_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-stone-800">Table {session.table_number}</span>
                        <span className="text-xs text-stone-400">{session.date}</span>
                        <Badge variant="outline" className="text-xs">{session.payments.length} encaissement{session.payments.length > 1 ? 's' : ''}</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                        {methods.map(m => { const I = METHOD_ICONS[m] || Receipt; return <span key={m} className="flex items-center gap-1"><I size={11} />{m}</span>; })}
                      </div>
                    </div>
                    <div className="text-right mr-4">
                      <p className="text-xl font-bold text-[#8b4513]">{session.total.toFixed(2)}&euro;</p>
                      <p className="text-[10px] text-stone-400 uppercase">Total session</p>
                    </div>
                    <div className="flex gap-1.5 items-center" onClick={e => e.stopPropagation()}>
                      <Button onClick={() => openEmailChoice(session)} variant="outline" size="sm" className="h-8 text-[10px] px-2.5 text-blue-600 border-blue-200 hover:bg-blue-50" data-testid={`session-email-${key}`}>
                        <Mail size={13} className="mr-1" /> Facture
                      </Button>
                    </div>
                    {expanded ? <ChevronUp size={18} className="text-stone-400" /> : <ChevronDown size={18} className="text-stone-400" />}
                  </div>

                  {/* Expanded: individual payments */}
                  {expanded && (
                    <div className="border-t border-stone-100 bg-stone-50/50">
                      {session.payments.map(payment => {
                        const MethodIcon = METHOD_ICONS[payment.method] || Receipt;
                        return (
                          <div key={payment.id} className="flex items-center gap-3 px-5 py-3 border-b border-stone-100 last:border-b-0" data-testid={`payment-row-${payment.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-semibold text-[#2c1810]">{payment.receipt_number}</span>
                                <span className="text-xs text-stone-400">{formatTimeGuyane(payment.created_at)}</span>
                                <Badge variant="outline" className="text-[10px] gap-1"><MethodIcon size={10} /> {payment.method}</Badge>
                              </div>
                              <p className="text-xs text-stone-500 mt-0.5">
                                {payment.customer_name || '-'} | Encaisse par: {payment.created_by_name || payment.created_by || '-'}
                              </p>
                              {(payment.items || []).length > 0 && (
                                <p className="text-[10px] text-stone-400 mt-0.5">
                                  {payment.items.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                                </p>
                              )}
                            </div>
                            <div className="font-bold text-[#8b4513] text-sm">{payment.amount?.toFixed(2)}&euro;</div>
                            <div className="flex gap-1">
                              <Button onClick={() => handlePrintReceipt(payment)} variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`print-receipt-${payment.id}`}>
                                <Printer size={13} />
                              </Button>
                              <Button onClick={() => { setEmailModal({ type: 'individual', payment }); setEmailAddress(''); setClientName(''); }} variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600" data-testid={`email-receipt-${payment.id}`}>
                                <Mail size={13} />
                              </Button>
                              {canDelete && (
                                <Button onClick={() => setDeleteModal(payment)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" data-testid={`delete-payment-${payment.id}`}>
                                  <Trash2 size={13} />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Email Modal */}
      <Dialog open={!!emailModal} onOpenChange={(o) => { if (!o) setEmailModal(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="email-modal">
          <DialogHeader>
            <DialogTitle>
              {emailModal?.type === 'choice' ? 'Envoyer la facture' :
               emailModal?.type === 'session' ? 'Facture complete de session' : 'Facture individuelle'}
            </DialogTitle>
          </DialogHeader>

          {emailModal?.type === 'choice' ? (
            <div className="space-y-3 py-2">
              <p className="text-sm text-stone-600">Table {emailModal.session.table_number} - {emailModal.session.payments.length} encaissements - {emailModal.session.total.toFixed(2)} EUR</p>
              <div className="space-y-2">
                <button
                  onClick={() => setEmailModal({ type: 'session', session: emailModal.session })}
                  className="w-full flex items-center gap-3 p-4 border-2 border-stone-200 rounded-xl hover:border-[#8B0000] hover:bg-red-50/30 transition-all text-left"
                  data-testid="choice-complete"
                >
                  <div className="w-10 h-10 bg-[#8B0000]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText size={20} className="text-[#8B0000]" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">Facture complete</p>
                    <p className="text-xs text-stone-500">Tous les articles et paiements de la session</p>
                  </div>
                </button>
                <button
                  onClick={() => setEmailModal({ type: 'select_individual', session: emailModal.session })}
                  className="w-full flex items-center gap-3 p-4 border-2 border-stone-200 rounded-xl hover:border-blue-400 hover:bg-blue-50/30 transition-all text-left"
                  data-testid="choice-individual"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Receipt size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-stone-800 text-sm">Facture individuelle</p>
                    <p className="text-xs text-stone-500">Choisir un encaissement specifique</p>
                  </div>
                </button>
              </div>
            </div>
          ) : emailModal?.type === 'select_individual' ? (
            <div className="space-y-2 py-2">
              <p className="text-sm text-stone-600 mb-2">Choisir l'encaissement a envoyer :</p>
              {emailModal.session.payments.map(p => (
                <button key={p.id}
                  onClick={() => setEmailModal({ type: 'individual', payment: p })}
                  className="w-full flex items-center justify-between p-3 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                  data-testid={`select-payment-${p.id}`}
                >
                  <div className="text-left">
                    <span className="font-mono text-xs font-semibold">{p.receipt_number}</span>
                    <span className="text-xs text-stone-400 ml-2">{p.method}</span>
                    <p className="text-[10px] text-stone-500">{(p.items || []).map(i => `${i.quantity}x ${i.name}`).join(', ')}</p>
                  </div>
                  <span className="font-bold text-[#8b4513]">{p.amount?.toFixed(2)}&euro;</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                {emailModal?.type === 'session' ? (
                  <>
                    <p><span className="font-semibold">Session :</span> Table {emailModal.session.table_number} - {emailModal.session.date}</p>
                    <p><span className="font-semibold">Total :</span> {emailModal.session.total.toFixed(2)} EUR ({emailModal.session.payments.length} encaissement{emailModal.session.payments.length > 1 ? 's' : ''})</p>
                  </>
                ) : (
                  <>
                    <p><span className="font-semibold">Recu :</span> {emailModal?.payment?.receipt_number}</p>
                    <p><span className="font-semibold">Montant :</span> {emailModal?.payment?.amount?.toFixed(2)} EUR</p>
                  </>
                )}
                <p className="text-xs text-amber-700 mt-1">La facture PDF sera jointe a l'email</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Nom pour la facture *</label>
                <Input type="text" placeholder="Nom du destinataire" value={clientName} onChange={(e) => setClientName(e.target.value)} data-testid="client-name-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Adresse email *</label>
                <Input type="email" placeholder="adresse@email.com" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} data-testid="email-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()} />
              </div>
            </div>
          )}

          {(emailModal?.type === 'session' || emailModal?.type === 'individual') && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailModal(null)} data-testid="email-cancel-btn">Annuler</Button>
              <Button onClick={handleSendEmail} disabled={!emailAddress || !clientName.trim() || sendingEmail} className="bg-[#8B0000] hover:bg-[#6d0000]" data-testid="email-send-btn">
                {sendingEmail ? 'Envoi...' : 'Envoyer la facture'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={!!deleteModal} onOpenChange={(o) => { if (!o) setDeleteModal(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="delete-modal">
          <DialogHeader><DialogTitle className="text-red-700">Deplacer dans la corbeille ?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
              <p><span className="font-semibold">Recu :</span> {deleteModal?.receipt_number}</p>
              <p><span className="font-semibold">Montant :</span> {deleteModal?.amount?.toFixed(2)} EUR</p>
            </div>
            <p className="text-xs text-amber-700 font-medium">Le paiement sera deplace dans la corbeille. Le webmaster peut le restaurer pendant 3 mois.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal(null)} data-testid="delete-cancel-btn">Annuler</Button>
            <Button onClick={handleDeletePayment} disabled={deleting} className="bg-red-700 hover:bg-red-800 text-white" data-testid="delete-confirm-btn">
              {deleting ? 'Suppression...' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset All Modal */}
      <Dialog open={resetModal} onOpenChange={(o) => { if (!o) setResetModal(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-modal">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle size={20} /> Reinitialiser TOUS les paiements ?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-100 border border-red-300 rounded-lg p-3">
              <p className="text-sm font-semibold text-red-800">ATTENTION : {payments.length} paiement(s) pour {payments.reduce((s, p) => s + (p.amount || 0), 0).toFixed(2)} EUR</p>
            </div>
            <p className="text-sm text-gray-600">Action irreversible.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetModal(false)} data-testid="reset-cancel-btn">Annuler</Button>
            <Button onClick={handleResetAll} disabled={resetting} className="bg-red-800 hover:bg-red-900 text-white" data-testid="reset-confirm-btn">
              {resetting ? 'Reinitialisation...' : 'Confirmer la RAZ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Trash Modal - Webmaster only */}
      <Dialog open={trashOpen} onOpenChange={(o) => { if (!o) setTrashOpen(false); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="trash-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Trash2 size={20} /> Corbeille ({trashItems.length} element{trashItems.length > 1 ? 's' : ''})
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">Les paiements sont conserves 3 mois avant effacement definitif.</p>
          {loadingTrash ? (
            <div className="text-center py-8 text-stone-400">Chargement...</div>
          ) : trashItems.length === 0 ? (
            <div className="text-center py-8 text-stone-400">La corbeille est vide</div>
          ) : (
            <div className="space-y-2 mt-2">
              {trashItems.map(item => (
                <div key={item.id} className="flex items-center gap-3 p-3 bg-stone-50 border border-stone-200 rounded-lg" data-testid={`trash-item-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold">{item.receipt_number}</span>
                      <Badge variant="outline" className="text-[10px]">T{item.table_number}</Badge>
                      <Badge variant="outline" className="text-[10px]">{item.method}</Badge>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {item.customer_name || '-'} | {item.amount?.toFixed(2)} EUR
                    </p>
                    <p className="text-[10px] text-red-500 mt-0.5">
                      Supprime par {item.deleted_by} ({item.deleted_by_role}) le {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString('fr-FR', { timeZone: 'America/Cayenne' }) : '-'}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleRestore(item.id)}
                    disabled={restoringId === item.id}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-xs"
                    data-testid={`restore-${item.id}`}
                  >
                    {restoringId === item.id ? 'Restauration...' : 'Restaurer'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentHistory;
